<template>
	<a-row type="flex" :gutter="16">
		<a-col :span="24" v-if="analisisAvailable && !imageError" class="mb-3">
			<a-alert type="info" :show-icon="false" banner>
				<div slot="message" class="d-flex align-items-center">
					<span>No info available, run analysis first</span>
					<a-button
						class="ml-auto"
						type="link"
						:loading="makingAnalisis"
						@click="submitAnalysis(post)"
					>
						Run AI Analysis
					</a-button>
				</div>
			</a-alert>
		</a-col>

		<a-col
			:span="24"
			v-else-if="
				(!isFaceAnalisis || !isOcrAnalisis || !isLabelAnalisis) && !imageError
			"
			class="mb-3"
		>
			<a-alert type="info" :show-icon="false" banner>
				<div slot="message" class="d-flex align-items-center">
					<span>Run complete analisis</span>
					<a-button
						class="ml-auto"
						type="link"
						:loading="makingAnalisis"
						@click="submitAnalysis(post)"
					>
						Run AI Analysis
					</a-button>
				</div>
			</a-alert>
		</a-col>

		<a-col :span="24" class="mb-2" v-if="!imageError">
			<div class="d-flex align-items-center">
				<a-radio-group :value="type" @change="typeChange">
					<a-radio-button
						value="FACE"
						:disabled="
							shapes.face.dots.length === 0 && shapes.face.poly.length === 0
						"
					>
						AI Face Expression
					</a-radio-button>
					<a-radio-button value="OCR" :disabled="shapes.ocr.poly.length === 0"
						>AI Text Recognition</a-radio-button
					>
				</a-radio-group>
				<div class="icon-wrapper">
					<a-icon type="minus-circle" />
					<a-slider class="width-200" :min="2" :max="8" v-model="border" />
					<a-icon type="plus-circle" />
				</div>
				<v-swatches
					v-model="color"
					show-fallback
					fallback-input-type="color"
				></v-swatches>
				<a-checkbox v-model="draw" class="ml-3">
					Draw
				</a-checkbox>
			</div>
		</a-col>
		<a-col :span="20" class="mb-3" v-show="!imageError">
			<div ref="container">
				<v-stage ref="stage" :config="stageSize">
					<v-layer ref="layer">
						<v-image :config="{ image }" />
						<div v-if="type === 'FACE' && draw">
							<template v-for="(innerShape, index) in shapes.face.dots">
								<template>
									<v-circle
										v-for="(circle, inner) in innerShape"
										:key="index + '_' + inner"
										:config="{
											x: circle.x * ratio,
											y: circle.y * ratio,
											radius: border,
											fill: color,
										}"
									/>
								</template>
							</template>
							<template v-for="(innerShape, index) in shapes.face.poly">
								<v-shape
									:config="{
										sceneFunc: function(context, shape) {
											context.beginPath()
											innerShape.forEach(v => {
												context.lineTo(v.x * ratio, v.y * ratio)
											})
											context.closePath()
											context.fillStrokeShape(shape)
										},

										stroke: color,
										strokeWidth: border,
									}"
									:key="index"
								/>
								<v-text
									:key="index + '_' + 'FACE'"
									:config="{
										x: innerShape[0].x * ratio,
										y: innerShape[0].y * ratio - 40,
										text: 'FACE ' + (index + 1),
										stroke: color,
										fill: color,
										strokeWidth: border - 2 <= 0 ? 1 : border - 2,
										fontSize: 25,
									}"
								/>
							</template>
						</div>
						<div v-if="type === 'OCR' && draw">
							<template v-for="(innerShape, index) in shapes.ocr.poly">
								<v-shape
									:config="{
										sceneFunc: function(context, shape) {
											context.beginPath()
											innerShape.forEach(v => {
												context.lineTo(v.x * ratio, v.y * ratio)
											})
											context.closePath()
											context.fillStrokeShape(shape)
										},

										stroke: color,
										strokeWidth: border,
									}"
									:key="index"
								/>
							</template>
						</div>
					</v-layer>
				</v-stage>
			</div>
		</a-col>

		<a-col :span="20" v-if="type === 'FACE' && !imageError">
			<h6 class="text-uppercase"><strong>Expressions</strong></h6>
			<a-row type="flex" :gutter="24" class="mb-3">
				<a-col
					:xs="12"
					:sm="12"
					:lg="8"
					:xxl="8"
					v-for="(item, index) in shapes.face.expression"
					:key="index"
				>
					<p class="text-dark text-capitalize">
						{{ 'face: ' + (index + 1) }}
					</p>

					<div class="d-flex flex-wrap">
						<span>Anger</span>
						<span
							class="ml-auto text-capitalize font-weight-bold text-gray-6"
							>{{ enumToValue(item.angerLikelihood) }}</span
						>
					</div>
					<div class="d-flex flex-wrap">
						<span>Joy</span>
						<span
							class="ml-auto text-capitalize font-weight-bold text-gray-6"
							>{{ enumToValue(item.joyLikelihood) }}</span
						>
					</div>
					<div class="d-flex flex-wrap">
						<span>Sorrow</span>
						<span
							class="ml-auto text-capitalize font-weight-bold text-gray-6"
							>{{ enumToValue(item.sorrowLikelihood) }}</span
						>
					</div>
					<div class="d-flex flex-wrap">
						<span>Surprise</span>
						<span
							class="ml-auto text-capitalize font-weight-bold text-gray-6"
							>{{ enumToValue(item.surpriseLikelihood) }}</span
						>
					</div>
					<div class="d-flex flex-wrap">
						<span>Blurred</span>
						<span
							class="ml-auto text-capitalize font-weight-bold text-gray-6"
							>{{ enumToValue(item.blurredLikelihood) }}</span
						>
					</div>
					<div class="d-flex flex-wrap">
						<span>Headwear</span>
						<span
							class="ml-auto text-capitalize font-weight-bold text-gray-6"
							>{{ enumToValue(item.headwearLikelihood) }}</span
						>
					</div>
					<div class="d-flex flex-wrap">
						<span>Underexposed</span>
						<span
							class="ml-auto text-capitalize font-weight-bold text-gray-6"
							>{{ enumToValue(item.underExposedLikelihood) }}</span
						>
					</div>
				</a-col>
			</a-row>
			<h6 class="text-uppercase"><strong>Metadata</strong></h6>
			<a-table
				:row-key="record => record.description"
				:columns="metadata"
				:data-source="shapes.labels"
				size="small"
				:pagination="false"
			>
				<span slot="score" slot-scope="record">
					{{ record.score.toFixed(2) }}
				</span>
				<!-- <span slot="topicality" slot-scope="record">
					{{ record.topicality.toFixed(2) }}
				</span> -->
			</a-table>
		</a-col>
		<a-col
			:span="20"
			v-if="!imageError && type === 'OCR' && shapes.ocr.brands.length > 1"
		>
			<h6 class="text-uppercase"><strong>TEXT</strong></h6>
			<template v-for="(item, index) in shapes.ocr.brands">
				<span
					:key="index"
					v-show="index > 0"
					class="badge text-blue text-uppercase bg-gray-2 font-size-12 mx-1 my-1"
				>
					{{ item }}
				</span>
			</template>
		</a-col>

		<a-col :span="24" v-if="imageError">
			<a-alert type="info" :show-icon="false" banner>
				<div slot="message" class="d-flex align-items-center">
					<span>No info available</span>
				</div>
			</a-alert>
		</a-col>
	</a-row>
</template>

<script>
import { mapActions } from 'vuex'
import * as ACTIONS from '@/store/actions'

export default {
	name: 'PostMediaAnalyze',

	props: {
		analisis: {
			type: Object,
			required: true,
		},
		post: {
			type: Object,
			required: true,
		},
	},
	data: function() {
		return {
			draw: true,
			border: 2,
			color: '#DDE6E8',
			stageSize: {
				height: 11000,
				width: 1000,
			},
			type: null,
			ratio: 0,
			loadingAnalisis: false,
			makingAnalisis: false,
			image: null,
			metadata: [
				{
					title: 'Name',
					dataIndex: 'description',
				},
				{
					title: 'Score',

					scopedSlots: { customRender: 'score' },
				},
			],
			imageError: false,
		}
	},

	created() {
		this.createImage(this.post.image)
		this.type = this.autoSelectAnalysis()
		window.addEventListener('resize', this.boxSize)
	},
	destroyed() {
		window.removeEventListener('resize', this.boxSize)
	},
	methods: {
		submitAnalysis() {
			this.makingAnalisis = true
			const promises = []
			const { id, image } = this.post

			promises.push(
				this.imageExpresison({
					postId: id,
					src: encodeURI(image),
				}).then(res => {
					return res.annotations
				}),
			)

			promises.push(
				this.imageOCR({
					postId: id,
					src: encodeURI(image),
				}).then(res => {
					return res.annotations
				}),
			)

			promises.push(
				this.imageLabels({
					postId: id,
					src: encodeURI(image),
				}).then(res => {
					return res.annotations
				}),
			)

			Promise.all(promises).then(res => {
				this.makingAnalisis = false
				let [face, ocr, labels] = res

				this.analisis.face = face
				this.analisis.ocr = ocr
				this.analisis.labels = labels
				this.type = this.autoSelectAnalysis()
			})
		},
		typeChange(e) {
			this.type = e.target.value
		},
		boxSize() {
			if (this.$refs.container.clientWidth > this.image.width) {
				this.ratio = Math.max(
					this.$refs.container.clientWidth / this.image.width,
					this.$refs.container.clientHeight / this.image.height,
				)
			} else {
				this.ratio = Math.min(
					this.$refs.container.clientWidth / this.image.width,
					this.$refs.container.clientHeight / this.image.height,
				)
			}

			const ratiowidth = this.image.width * this.ratio
			const ratioHeight = this.image.height * this.ratio

			this.image.width = ratiowidth
			this.image.height = ratioHeight
			this.stageSize.width = ratiowidth
			this.stageSize.height = ratioHeight
		},

		createImage(src) {
			const image = new window.Image()
			image.src = src

			image.onload = () => {
				this.image = image

				this.ratio = Math.min(
					this.$refs.container.clientWidth / image.width,
					this.$refs.container.clientHeight / image.height,
				)

				const ratiowidth = this.image.width * this.ratio
				const ratioHeight = this.image.height * this.ratio

				this.image.width = ratiowidth
				this.image.height = ratioHeight
				this.stageSize.width = ratiowidth
				this.stageSize.height = ratioHeight
			}

			image.onerror = () => {
				this.imageError = true
				image.src = '/resources/images/gray_not_found.png'
			}
		},

		autoSelectAnalysis() {
			if (this.isFaceAnalisis) {
				return 'FACE'
			} else if (this.isOcrAnalisis) {
				return 'OCR'
			} else {
				return null
			}
		},
		enumToValue(val) {
			switch (val) {
				case 0:
					return 'UNKNOWN'
				case 1:
					return 'VERY UNLIKELY'
				case 2:
					return 'UNLIKELY'
				case 3:
					return 'POSSIBLE'
				case 4:
					return 'LIKELY'
				case 5:
					return 'VERY LIKELY'
				default:
					return 0
			}
		},
		...mapActions({
			imageAnalysis: ACTIONS.ANALYSIS.IMAGE,
			imageOCR: ACTIONS.ANALYSIS.IMAGE_OCR,
			imageExpresison: ACTIONS.ANALYSIS.IMAGE_EXPRESSION,
			imageLabels: ACTIONS.ANALYSIS.IMAGE_LABELS,
		}),
	},
	computed: {
		analisisAvailable() {
			return (
				!this.isFaceAnalisis && !this.isOcrAnalisis && !this.isLabelAnalisis
			)
		},

		isFaceAnalisis() {
			return this.analisis && this.analisis.face && this.analisis.face !== null
		},
		isOcrAnalisis() {
			return this.analisis && this.analisis.ocr && this.analisis.ocr !== null
		},

		isLabelAnalisis() {
			return (
				this.analisis && this.analisis.labels && this.analisis.labels !== null
			)
		},

		shapes() {
			const face = Array.isArray(this.analisis.face)
			const ocr = Array.isArray(this.analisis.ocr)
			const labels = Array.isArray(this.analisis.labels)

			let shapes = {
				face: {
					poly: [],
					dots: [],
					expression: [],
				},
				ocr: {
					poly: [],
					brands: [],
				},
				labes: [],
			}

			if (face) {
				shapes.face.poly = this.analisis.face.map(face => {
					return face.fdBoundingPoly.vertices
				})
				shapes.face.dots = this.analisis.face.map(face => {
					return face.landmarks.map(land => {
						return land.position
					})
				})
				shapes.face.expression = this.analisis.face.map(face => {
					let {
						joyLikelihood,
						sorrowLikelihood,
						angerLikelihood,
						surpriseLikelihood,
						underExposedLikelihood,
						blurredLikelihood,
						headwearLikelihood,
					} = face
					return {
						joyLikelihood,
						sorrowLikelihood,
						angerLikelihood,
						surpriseLikelihood,
						underExposedLikelihood,
						blurredLikelihood,
						headwearLikelihood,
					}
				})
			}

			if (labels) {
				shapes.labels = this.analisis.labels
			}

			if (ocr) {
				shapes.ocr.poly = this.analisis.ocr.map(text => {
					return text.boundingPoly.vertices
				})
				shapes.ocr.brands = this.analisis.ocr.map(text => {
					return text.description
				})
			}

			return shapes
		},
	},
}
</script>

<style></style>
